import React, { useEffect, useState } from "react";
import SessionTracker from "../session/App";
import { BreakManagerModal } from "../session/features/sessionWatch/features/sessionTimeline/BreakManagerModal";
import { FeelStep } from "./FeelStep";
import { useSelector, useDispatch } from "react-redux";
import {
  selectBuckets,
  selectCalendarTodos,
  selectCurrentDate,
  selectMinimizeCategory,
  selectStep,
  selectTimeFrame,
  setBucketList,
  setCalendarTodos,
  setFeelData,
  setCurrentDate,
  setMinimizeCategories,
  setStep,
  setTimeFrame,
  selectCloseOption,
  setCloseOption,
  selectBucketViewIsOn,
} from "./slice/buckets";
import {
  addChildToLucket,
  getCurrentDateFromLuckets,
  getSpreadsheetData,
  updateBucket,
} from "./slice/functions";
import { filteredBucketList } from "./slice/lucket-model/filterBuckets";
import { onCloseDay } from "./slice/lucket-model/closeAndOpenDay";
import {
  STEP_OPTION,
  CATEGORIES,
  APP_BACKGROUND_COLOR,
  BUCKETLIST_CONTAINER_BACKGROUND_COLOR,
} from "./constants";
import "./index.css";
import { AllTimes } from "./AllTimes.cmp";
import NavBar from "./nav";
import SpreadsheetTableContainer from "../table/SpreadsheetTable";
import { useOfflineSync } from "./hooks/useOfflineSync";
import { BucketCategory } from "./BucketCategory";
import { ExpandedBucketView } from "./ExpandedBucketView";
import { useTimeSlots } from "./hooks/useTimeSlots";
import axios from "axios";
import BucketView from "./BucketView";
import { EditExtension } from "./edit-extension-new/edit-extension";
import { useNavigate } from "react-router-dom";

export const BucketList = ({ user, handleLogout }) => {
  const { openTimes, setOpenTimes, expandAllTimes, collapseAllTimes } = useTimeSlots();
  const offlineNotification = useOfflineSync();

  const [expandedBucket, setExpandedBucket] = useState("");
  const [showDescription, setShowDescription] = useState(false);
  const [showSessionTracker, setShowSessionTracker] = useState(false);
  const [showBreakManager, setShowBreakManager] = useState(false);
  const [addBucketOpen, setAddBucketOpen] = useState(false);
  const [openaiResponse, setOpenaiResponse] = useState(null);
  const [showSpreadsheetTable, setShowSpreadsheetTable] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [expandedTodos, setExpandedTodos] = useState({});

  const buckets = useSelector(selectBuckets);
  const todosFromCalendar = useSelector(selectCalendarTodos) || [];
  const step = useSelector(selectStep);
  const timeFrame = useSelector(selectTimeFrame);
  const currentDate = useSelector(selectCurrentDate);
  const minimizeCategories = useSelector(selectMinimizeCategory);
  const closeOption = useSelector(selectCloseOption);
  const bucketViewIsOn = useSelector(selectBucketViewIsOn);

  const dispatch = useDispatch();

  const navigate = useNavigate();
  const handleRefreshLogin = async () => {
    await handleLogout();
    navigate('/login');
  }

  // Initialize current date from buckets
  useEffect(() => {
    if (!currentDate && buckets.length > 0) {
      let newCurrentDate = getCurrentDateFromLuckets(buckets);
      dispatch(setCurrentDate(newCurrentDate));
    }
  }, [buckets, currentDate, dispatch]);

  // Fetch spreadsheet and calendar data on mount
  useEffect(() => {
    const fetchData = async () => {
      const data = await getSpreadsheetData();
      if (data) {
        dispatch(setBucketList(data.values));
      }

      // Fetch calendar events separately
      try {
        const calendarResponse = await axios.get(`${process.env.REACT_APP_PROD_SERVER_HOST}/calendar/today`, {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('firebaseIdToken')}::GAT::${localStorage.getItem('googleAccessToken')}`
          }
        });
        const calendarTodos = calendarResponse.data.todos || [];
        dispatch(setCalendarTodos(calendarTodos));
        // Cache calendar data
        const userEmail = user?.email || 'anonymous';
        localStorage.setItem(`calendar-todos_${userEmail}`, JSON.stringify(calendarTodos));
        console.log(`✅ Cached calendar data for user: ${userEmail}`);
      } catch (calendarError) {
        console.warn('Failed to fetch calendar events, using cached data if available:', calendarError);
        const userEmail = user?.email || 'anonymous';
        const storedCalendar = localStorage.getItem(`calendar-todos_${userEmail}`);
        if (storedCalendar) {
          dispatch(setCalendarTodos(JSON.parse(storedCalendar)));
        }
      }

      // Fetch feel data
      try {
        const feelResponse = await axios.get(`${process.env.REACT_APP_PROD_SERVER_HOST}/feel/data`, {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('firebaseIdToken')}::GAT::${localStorage.getItem('googleAccessToken')}`
          }
        });
        dispatch(setFeelData(feelResponse.data.feelData || {}));
      } catch (feelError) {
        console.warn('Failed to fetch feel data:', feelError);
      }
    };
    fetchData();
  }, [dispatch, user]);

  const dispatchSetTimeFrame = (tf) => dispatch(setTimeFrame(tf));
  const dispatchSetStep = (newStep) => {
    // When Time step is selected, show the Session Tracker
    dispatch(setStep(newStep));
  };
  const dispatchSetCloseOption = (closeStyle) => dispatch(setCloseOption(closeStyle));

  const addChild = async (lucket, childLuckets) => {
    let result = await addChildToLucket(lucket, childLuckets, buckets);
    dispatch(setBucketList(result.data));
  };

  const toggleExpandBucket = (bucketId) => {
    setExpandedBucket(bucketId === expandedBucket ? "" : bucketId);
  };

  const handleToggleMinimize = (category) => {
    const isMinimized = minimizeCategories.includes(category);
    if (isMinimized) {
      dispatch(setMinimizeCategories(minimizeCategories.filter(c => c !== category)));
    } else {
      dispatch(setMinimizeCategories([...minimizeCategories, category]));
    }
  };

  const closeDay = () => (
    <div
      className="confirmClose"
      onClick={async () => {
        let result = await onCloseDay(buckets, currentDate, closeOption);
        dispatch(setBucketList(result.data));
        let newCurrentDate = getCurrentDateFromLuckets(result.data);
        dispatch(setCurrentDate(newCurrentDate));
        dispatch(setStep(STEP_OPTION.Plan));
      }}
    >
      Confirm Close
    </div>
  );

  const handlePromptOpenAI = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_PROD_SERVER_HOST}/openai/prompt`, { buckets }, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('firebaseIdToken')}::GAT::${localStorage.getItem('googleAccessToken')}`
        }
      });
      const parser = new DOMParser();
      const doc = parser.parseFromString(response.data, 'text/html');
      setOpenaiResponse(doc.body.innerHTML);
    } catch (error) {
      console.error('Error prompting OpenAI:', error);
    }
  };

  // Filter buckets
  const filteredBuckets = currentDate
    ? filteredBucketList(buckets, step, timeFrame, currentDate)
    : buckets.filter(b => b.date === getCurrentDateFromLuckets(buckets));

  // For Review step, get Plan and Do buckets separately
  const planBuckets = currentDate
    ? filteredBucketList(buckets, STEP_OPTION.Plan, timeFrame, currentDate)
    : [];
  const doBuckets = currentDate
    ? filteredBucketList(buckets, STEP_OPTION.Do, timeFrame, currentDate)
    : [];

  const renderBucketsByCategory = () => {
    const expandedBucketItem = filteredBuckets.find(b => b.rowID === expandedBucket);

    if (expandedBucket && expandedBucketItem) {
      const childLuckets = filteredBuckets.filter(b => b.parentRowID === expandedBucket);

      return (
        <ExpandedBucketView
          bucket={expandedBucketItem}
          childLuckets={childLuckets}
          buckets={buckets}
          currentDate={currentDate}
          showDescription={showDescription}
          onToggleDescription={() => setShowDescription(!showDescription)}
          onClose={() => toggleExpandBucket("")}
          onAddChild={() => addChild(expandedBucketItem, childLuckets)}
        />
      );
    }

    return <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {CATEGORIES.map((category) => (
        <BucketCategory
          key={category}
          category={category}
          filteredBuckets={filteredBuckets}
          buckets={buckets}
          currentDate={currentDate}
          isMinimized={minimizeCategories.includes(category)}
          onToggleMinimize={() => handleToggleMinimize(category)}
          onExpandBucket={toggleExpandBucket}
        />
      ))}
    </div>
  };

  return !currentDate ? <div>
    "Please login again. If you have just logged in, please wait a few seconds."
    <button onClick={handleRefreshLogin}>Login</button>
  </div> : (
    <div key={buckets.length + "all buckets"} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Offline/Sync Notification */}
      {offlineNotification && (
        <div className={`sync-notification ${offlineNotification.type}`}>
          {offlineNotification.message}
        </div>
      )}

      <NavBar

        showSessionTracker={showSessionTracker}
        setShowSessionTracker={setShowSessionTracker}
        showBreakManager={showBreakManager}
        setShowBreakManager={setShowBreakManager}
        user={user}
        handleLogout={handleLogout}
        setAddBucketOpen={setAddBucketOpen}
        addBucketOpen={addBucketOpen}
        getSpreadsheetData={getSpreadsheetData}
        closeDay={closeDay}
        step={step}
        dispatchSetStep={dispatchSetStep}
        timeFrame={timeFrame}
        dispatchSetTimeFrame={dispatchSetTimeFrame}
        closeOption={closeOption}
        dispatchSetCloseOption={dispatchSetCloseOption}
        currentDate={currentDate}
        buckets={buckets}
        handlePromptOpenAI={handlePromptOpenAI}
        showSpreadsheetTable={showSpreadsheetTable} // Pass state to NavBar
        setShowSpreadsheetTable={setShowSpreadsheetTable} // Pass state setter to NavBar
        showCalendar={showCalendar}
        setShowCalendar={setShowCalendar}
      />
      {(step === STEP_OPTION.Plan || step === STEP_OPTION.Review) && <div style={{ justifyContent: "center", display: "flex", flexDirection: "row", alignItems: "flex-start", gap: "10px" }}>
        {showCalendar && todosFromCalendar.length > 0 && (<div className="todosFromCalendarSection">
          <h2>📅 Events planned in Calendar</h2>
          <ul>{todosFromCalendar.map(todo => (
            <li key={todo.id}>
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={() => setExpandedTodos(prev => ({ ...prev, [todo.id]: !prev[todo.id] }))}>
                <span>{expandedTodos[todo.id] ? '▼' : '▶'}</span>
                <strong>{todo.text}</strong>
              </div>
              {expandedTodos[todo.id] && (
                <>
                  <small>
                    Start: {new Date(todo.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {' | '}
                    End: {new Date(todo.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </small>
                  {todo.description && (
                    <>
                      <br />
                      <small>{todo.description}</small>
                    </>
                  )}
                </>
              )}
            </li>
          ))}</ul>
        </div>)}
        {step !== STEP_OPTION.Time && (showSpreadsheetTable && <SpreadsheetTableContainer isExpanded={expandedBucket} timeFrame={timeFrame} />)}

      </div>}

      {
        step === STEP_OPTION.Time && <SessionTracker
          setShowSessionTracker={setShowSessionTracker}
          showBreakManager={showBreakManager}
          setShowBreakManager={setShowBreakManager}
        />
      }
      {
        step !== STEP_OPTION.Time && (openaiResponse && <div
          className="openai-response"
          dangerouslySetInnerHTML={{ __html: openaiResponse }}
        />)
      }
      {
        step !== STEP_OPTION.Time && !openaiResponse && (step === STEP_OPTION.Feel ? (
          <FeelStep />
        ) : (
          <div className="bucketListBody" style={{ flex: 9, backgroundColor: step != STEP_OPTION.Review && APP_BACKGROUND_COLOR }}>

            <div className="bucketListContainer" style={{ margin: bucketViewIsOn ? "0px" : "20px", width: bucketViewIsOn && "100%", height: bucketViewIsOn && "100%", backgroundColor: step != STEP_OPTION.Review && BUCKETLIST_CONTAINER_BACKGROUND_COLOR }}>
              <div style={{ flexDirection: "row", display: !expandedBucket ? "flex" : "block", height: bucketViewIsOn && "100%", }}>
                {filteredBuckets &&
                  (step == STEP_OPTION.Review ? (
                    <div style={{ display: "flex", gap: "0px", width: "100%" }}>
                      <div style={{ flex: 1, backgroundColor: BUCKETLIST_CONTAINER_BACKGROUND_COLOR, padding: "0 15px" }}>
                        <h3 style={{ textAlign: "center", marginBottom: "20px", marginTop: "10px", color: "#333" }}>📋 Plan</h3>
                        {CATEGORIES.map((category) => (
                          <BucketCategory
                            key={`plan-${category}`}
                            category={category}
                            filteredBuckets={planBuckets}
                            buckets={buckets}
                            currentDate={currentDate}
                            isMinimized={minimizeCategories.includes(category)}
                            onToggleMinimize={() => handleToggleMinimize(category)}
                            onExpandBucket={toggleExpandBucket}
                          />
                        ))}
                      </div>
                      <div style={{ width: "3px", backgroundColor: "#999", alignSelf: "stretch" }}></div>
                      <div style={{ flex: 1, backgroundColor: BUCKETLIST_CONTAINER_BACKGROUND_COLOR, padding: "0 15px" }}>
                        <h3 style={{ textAlign: "center", marginBottom: "20px", marginTop: "10px", color: "#333" }}>✅ Do</h3>
                        <div>
                          <img
                            src="/images/up-arrow.svg"
                            onClick={() => expandAllTimes()}
                            style={{ height: "20px", width: "20px", marginRight: "30px" }}
                          />
                          <img
                            src="/images/up-arrow.svg"
                            onClick={() => collapseAllTimes()}
                            style={{ transform: "rotate(180deg)", height: "20px", width: "20px" }}
                          />
                          <AllTimes
                            filteredBuckets={doBuckets}
                            open={openTimes}
                            setOpen={setOpenTimes}
                          />
                        </div>
                      </div>
                    </div>
                  ) : step == STEP_OPTION.Do ? (
                    <div>
                      <img
                        src="/images/up-arrow.svg"
                        onClick={() => expandAllTimes()}
                        style={{ height: "20px", width: "20px", marginRight: "30px" }}
                      />
                      <img
                        src="/images/up-arrow.svg"
                        onClick={() => collapseAllTimes()}
                        style={{ transform: "rotate(180deg)", height: "20px", width: "20px" }}
                      />
                      <AllTimes
                        filteredBuckets={filteredBuckets}
                        open={openTimes}
                        setOpen={setOpenTimes}
                      />
                    </div>

                  ) : (bucketViewIsOn && !expandedBucket) ? <BucketView toggleExpandBucket={toggleExpandBucket} buckets={filteredBuckets} /> : (
                    renderBucketsByCategory()
                  ))}

                {(bucketViewIsOn && !expandedBucket) && <EditExtension updateBucket={updateBucket} buckets={filteredBuckets} />}
              </div>
            </div>
          </div>
        ))
      }      {/* Break Manager Modal */}
      <BreakManagerModal
        isOpen={showBreakManager}
        onClose={() => setShowBreakManager(false)}
      />
    </div >
  );
};
